import { Component } from '@theme/component';
import { mediaQueryLarge, prefersReducedMotion } from '@theme/utilities';
import { ThemeEvents, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLElement} [modelFitCaption] - Mobile model sizing caption.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    target?.addEventListener('click', this.#handleVariantSwatchClick, { capture: true, signal });
    this.#prepareVariantColorMediaMap();
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.refs.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    
    requestAnimationFrame(() => this.#syncModelFitCaption());
    setTimeout(() => this.#syncModelFitCaption(), 300);
    setTimeout(() => this.#syncModelFitCaption(), 1000);

    const slideshow = this.refs.slideshow;
        const scroller = slideshow?.querySelector('[ref="scroller"]');

    if (scroller && !scroller.__aurrevaFulbixScrollPatch) {
      const originalScrollTo = scroller.scrollTo.bind(scroller);

      scroller.__aurrevaFulbixScrollPatch = true;

      scroller.scrollTo = function (...args) {
        const stack = new Error().stack || '';

        // Fulbix resets the gallery to the first slide before Horizon
        // selects the media associated with the new variant.
       if (
  /scrollToFirstSlide/i.test(stack) &&
  /variant-image-swatch|fulbix-variant-images/i.test(stack)
) {
  return;
}

        return originalScrollTo(...args);
      };
    }
    const grid = this.querySelector('.media-gallery__grid');

    if (grid && !grid.__aurrevaFulbixGridPatch) {
      const originalAppendChild = grid.appendChild.bind(grid);

      grid.__aurrevaFulbixGridPatch = true;

      grid.appendChild = (child) => {
        const isExistingMediaItem =
          child instanceof HTMLElement &&
          child.dataset.mediaId &&
          child.parentElement === grid;

        // Empêche FulbiX de remettre brièvement la grille
        // dans son ordre initial lors d'un changement de couleur.
        if (
          mediaQueryLarge.matches &&
          this.presentation === 'grid' &&
          this.#pendingVariantMediaPlacement &&
          isExistingMediaItem
        ) {
          return child;
        }

        return originalAppendChild(child);
      };
    }

    if (slideshow) {
      const observer = new MutationObserver(() => this.#syncModelFitCaption());
      observer.observe(slideshow, {
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-hidden'],
      });
      this.#modelFitObserver = observer;
    }

    if (this.hasAttribute('data-focus-selected-media')) {
  this.removeAttribute('data-focus-selected-media');
  this.#scrollSelectedMediaIntoView();
}
  }

  #controller = new AbortController();
  #modelFitObserver;
  #pendingVariantMediaPlacement;
  #variantColorMediaMap = new Map();
#variantMediaAnimation;

  disconnectedCallback() {
  super.disconnectedCallback();

  this.#modelFitObserver?.disconnect();
  this.#controller.abort();
}

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
  if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

  const changedOption = Array.from(event.target.querySelectorAll('[data-option-value-id]')).find(
    (option) => option instanceof HTMLElement && option.dataset.optionValueId === event.detail?.optionValueId
  );

  const preserveScrollPosition =
    changedOption?.dataset.optionName?.trim().toLowerCase() === 'size';

  event.promise
    .then(({ detail }) => {
      if (!detail?.html) return;

      const { html } = detail;
      const newMediaGallery = html.querySelector('media-gallery');
      if (!newMediaGallery) return;

      const nextMediaId = newMediaGallery.dataset.selectedMediaId;
      if (!nextMediaId) return;
      const previousMediaId = this.dataset.selectedMediaId;

      // Update the selected media ID without replacing the whole gallery.
      this.dataset.selectedMediaId = nextMediaId;

      const slides = Array.from(
        this.querySelectorAll('slideshow-slide[data-media-id]')
      );

      const selectedIndex = slides.findIndex(
        (slide) => slide.dataset.mediaId === nextMediaId
      );

      if (selectedIndex < 0) {
        this.replaceWith(newMediaGallery);
        return;
      }

      // Size changes must not move the customer's current position.
      if (preserveScrollPosition) return;

      if (mediaQueryLarge.matches && this.presentation === 'grid') {
  const pendingPlacement = this.#pendingVariantMediaPlacement;

  // Changement de couleur FulbiX :
  // garder la page immobile et déplacer l'image sélectionnée
  // vers la cellule visible la plus proche du clic.
  if (pendingPlacement) {
  const alreadyPlacedInstantly =
    pendingPlacement.instantMediaId === nextMediaId;

  if (
    !alreadyPlacedInstantly &&
    nextMediaId !== previousMediaId
  ) {
    this.#placeSelectedMediaAtViewportPosition(
      pendingPlacement.clientY
    );
  }

  this.#pendingVariantMediaPlacement = undefined;
  return;
}

  // Conserver le comportement existant pour les autres sélections.
  this.#scrollSelectedMediaIntoView();
  return;
}

      // Mobile: use Horizon's native slideshow navigation.
      this.slideshow?.select(selectedIndex, undefined, {
        animate: true,
      });
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') {
        console.warn('[media-gallery] Event promise rejected:', error);
      }
    });
};

/**
 * Keeps the mobile model sizing caption in sync with the active gallery slide.
 * @param {SlideshowSelectEvent} event - The slideshow select event.
 */
#handleSlideshowSelect = (event) => {
    const slide = event.detail?.slide ?? this.querySelector('slideshow-slide.product-media-container[aria-hidden="false"]');
    if (!(slide instanceof HTMLElement)) return;

    this.#updateModelFitCaption(slide.dataset.modelFitCaption ?? '');
  };

  #syncModelFitCaption() {
    const slides = Array.from(
      this.querySelectorAll('slideshow-slide.product-media-container[data-media-id]')
    );

    const activeSlide =
      slides.find((slide) => slide.getAttribute('aria-hidden') !== 'true') ??
      slides.find((slide) => slide.dataset.modelFitCaption) ??
      null;

    const caption = activeSlide instanceof HTMLElement
      ? activeSlide.dataset.modelFitCaption ?? ''
      : '';

    this.#updateModelFitCaption(caption);
  }

  /**
   * @param {string} value - Caption text for the active media, or an empty string.
   */
  #updateModelFitCaption(value) {
    const captionElement =
      this.refs.modelFitCaption ??
      this.querySelector('[ref="modelFitCaption"]');

    if (!captionElement) return;

    const caption = value.trim();
    captionElement.textContent = caption;
    captionElement.hidden = caption === '';
  }

  async #prepareVariantColorMediaMap(attempt = 0) {
  try {
    const root =
      this.closest('.shopify-section, dialog') ?? document;

    const variantPicker = root.querySelector(
      'variant-picker[data-product-url]'
    );

    if (!(variantPicker instanceof HTMLElement)) {
  if (attempt < 8) {
    setTimeout(() => {
      if (this.isConnected) {
        this.#prepareVariantColorMediaMap(attempt + 1);
      }
    }, 150);
  }

  return;
}

    const productUrl = new URL(
      variantPicker.dataset.productUrl,
      window.location.origin
    );

    productUrl.search = '';

    if (!productUrl.pathname.endsWith('.js')) {
      productUrl.pathname =
        `${productUrl.pathname.replace(/\/$/, '')}.js`;
    }

    const response = await fetch(productUrl.toString(), {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) return;

    const product = await response.json();

    const normalizeUrl = (url) => {
      if (!url) return null;

      try {
        return new URL(
          url,
          window.location.origin
        ).pathname;
      } catch {
        return null;
      }
    };

    const colorInputs = Array.from(
      root.querySelectorAll(
        'variant-picker input[data-option-name]'
      )
    ).filter((input) =>
      /color|colour|couleur/i.test(
        input.dataset.optionName || ''
      )
    );

    if (colorInputs.length === 0) {
  if (attempt < 8) {
    setTimeout(() => {
      if (this.isConnected) {
        this.#prepareVariantColorMediaMap(attempt + 1);
      }
    }, 150);
  }

  return;
}

    this.#variantColorMediaMap.clear();

    for (const input of colorInputs) {
      if (!(input instanceof HTMLInputElement)) continue;

      const color = input.value
        ?.trim()
        .toLowerCase();

      const variantId =
        input.dataset.variantId;

      if (!color || !variantId) continue;

      const variant = product.variants?.find(
        (item) =>
          String(item.id) === String(variantId)
      );

      if (!variant) continue;

      const imageId =
        variant.featured_image?.id;

      const imageSrc =
        variant.featured_image?.src;

      const media = product.media?.find(
        (item) =>
          String(item.preview_image?.id) ===
            String(imageId) ||
          normalizeUrl(item.preview_image?.src) ===
            normalizeUrl(imageSrc)
      );

      if (!media?.id) continue;

      this.#variantColorMediaMap.set(
        color,
        String(media.id)
      );
    }

    // Précharge les images des couleurs afin que
    // leur apparition soit immédiate au clic.
    const grid = this.querySelector(
      '.media-gallery__grid'
    );

    if (!(grid instanceof HTMLElement)) return;

    for (
      const mediaId of
      this.#variantColorMediaMap.values()
    ) {
      const mediaItem = Array.from(
        grid.children
      ).find(
        (item) =>
          item instanceof HTMLElement &&
          item.dataset.mediaId === mediaId
      );

      const image =
        mediaItem?.querySelector('img');

      if (!(image instanceof HTMLImageElement)) {
        continue;
      }

      const preload = new Image();

      if (image.srcset) {
        preload.srcset = image.srcset;
      }

      if (image.sizes) {
        preload.sizes = image.sizes;
      }

      preload.src =
        image.currentSrc || image.src;
    }
  } catch (error) {
    console.warn(
      '[media-gallery] Variant color media map could not be prepared.',
      error
    );
  }
}

#animateVariantMedia(target) {
  if (
    !(target instanceof HTMLElement) ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return;
  }

  const visual =
    target.querySelector('img') ||
    target.querySelector('.product-media') ||
    target;

  this.#variantMediaAnimation?.cancel();

  this.#variantMediaAnimation = visual.animate(
    [
      {
        opacity: 0.08,
        transform: 'translateY(10px) scale(0.97)',
      },
      {
        opacity: 0.72,
        transform: 'translateY(2px) scale(0.992)',
        offset: 0.62,
      },
      {
        opacity: 1,
        transform: 'translateY(0) scale(1)',
      },
    ],
    {
      duration: 420,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
      fill: 'both',
    }
  );
}

  #handleVariantSwatchClick = (event) => {
  if (
    !mediaQueryLarge.matches ||
    this.presentation !== 'grid' ||
    !(event.target instanceof Element)
  ) {
    return;
  }

  const swatch = event.target.closest('.fulbix-swatch');
  if (!swatch) return;

  const pendingPlacement = {
    clientY: event.clientY,
  };

  this.#pendingVariantMediaPlacement = pendingPlacement;

  /*
   * Optimisation AURREVA :
   * si le mapping couleur → mediaId est déjà prêt,
   * déplacer immédiatement l'image sans attendre
   * le traitement Shopify / FulbiX.
   */
  const color = swatch.dataset.value
    ?.trim()
    .toLowerCase();

  const mediaId = color
    ? this.#variantColorMediaMap.get(color)
    : undefined;

  if (mediaId) {
    const target =
      this.#placeSelectedMediaAtViewportPosition(
        event.clientY,
        mediaId
      );

    if (target) {
  pendingPlacement.instantMediaId = mediaId;
  this.#animateVariantMedia(target);
}
  }

  /*
   * Sécurité :
   * conserver temporairement l'état pendant que
   * FulbiX / Shopify terminent le changement de variante.
   */
  setTimeout(() => {
    if (
      this.#pendingVariantMediaPlacement ===
      pendingPlacement
    ) {
      this.#pendingVariantMediaPlacement = undefined;
    }
  }, 2000);
};

/**
 * Place le média de la variante sélectionnée dans la cellule visible
 * la plus proche de la hauteur où la cliente a cliqué.
 *
 * @param {number} referenceY - Position verticale du clic dans l'écran.
 */
#placeSelectedMediaAtViewportPosition(referenceY, mediaId = this.dataset.selectedMediaId) {
  const grid = this.querySelector('.media-gallery__grid');
  

  if (!(grid instanceof HTMLElement) || !mediaId) return;

  const items = Array.from(grid.children).filter(
    (item) => item instanceof HTMLElement && item.dataset.mediaId
  );

  const target = items.find(
    (item) => item.dataset.mediaId === mediaId
  );

  if (!(target instanceof HTMLElement)) return;

  const visibleItems = items
    .map((item) => ({
      item,
      rect: item.getBoundingClientRect(),
    }))
    .filter(
      ({ rect }) =>
        rect.bottom > 0 &&
        rect.top < window.innerHeight
    );

  if (visibleItems.length === 0) return;

  visibleItems.sort((a, b) => {
    const centerA =
      (a.rect.top + a.rect.bottom) / 2;

    const centerB =
      (b.rect.top + b.rect.bottom) / 2;

    const distanceDifference =
      Math.abs(centerA - referenceY) -
      Math.abs(centerB - referenceY);

    if (distanceDifference !== 0) {
      return distanceDifference;
    }

    // Si les deux images sont sur la même ligne,
    // préférer celle de gauche.
    return a.rect.left - b.rect.left;
  });

  const anchor = visibleItems[0]?.item;

  if (!(anchor instanceof HTMLElement)) {
  return;
}

if (anchor === target) {
  return target;
}

  const targetMarker =
    document.createComment('aurreva-variant-media-target');

  const anchorMarker =
    document.createComment('aurreva-variant-media-anchor');

  target.replaceWith(targetMarker);
  anchor.replaceWith(anchorMarker);

  targetMarker.replaceWith(anchor);
  anchorMarker.replaceWith(target);
  return target;
}

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #scrollSelectedMediaIntoView() {

  if (!mediaQueryLarge.matches || this.presentation !== 'grid') return;

  requestAnimationFrame(() => {
    if (!this.isConnected) return;

    const mediaId = this.dataset.selectedMediaId;
    if (!mediaId) return;

    const target = this.querySelector(
      `.media-gallery__grid [data-media-id="${mediaId}"]`
    );

    if (!(target instanceof HTMLElement)) return;

    const headerHeight =
      parseFloat(
        getComputedStyle(document.documentElement)
          .getPropertyValue('--header-height')
      ) || 0;

    const rect = target.getBoundingClientRect();

    const alreadyVisible =
      rect.top >= headerHeight &&
      rect.bottom <= window.innerHeight;

    if (alreadyVisible) return;

    target.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'center',
    });
  });
}
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
